import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ATID } from "@/lib/adminTestIds";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Bot, Zap, Loader2 } from "lucide-react";
import AutomationSourcesTab from "./automation/AutomationSourcesTab";
import AutomationInboxTab from "./automation/AutomationInboxTab";
import AutomationRunsTab from "./automation/AutomationRunsTab";
import AutomationLogsTab from "./automation/AutomationLogsTab";
import AutomationSettingsTab from "./automation/AutomationSettingsTab";
import AutomationDashboardTab from "./automation/AutomationDashboardTab";
import AutomationAiConfigTab from "./automation/AutomationAiConfigTab";
import AutomationReviewTab from "./automation/AutomationReviewTab";

const TABS = [
  { key: "dashboard", label: "Panel general" },
  { key: "sources", label: "Fuentes" },
  { key: "inbox", label: "Noticias detectadas" },
  { key: "review", label: "Pendientes de revisión" },
  { key: "runs", label: "Historial de ejecuciones" },
  { key: "settings", label: "Configuración" },
  { key: "ai", label: "Configuración de IA" },
  { key: "logs", label: "Registros de errores" },
];

export default function AdminAutomationPage() {
  const [tab, setTab] = useState("dashboard");
  const [counts, setCounts] = useState({ total: 0 });
  const [sources, setSources] = useState([]);
  const [runningAll, setRunningAll] = useState(false);

  const refreshCounts = () =>
    api.adminAutoImportedCounts().then(setCounts).catch(() => {});
  const refreshSources = () =>
    api.adminAutoListSources().then((r) => setSources(r.items || [])).catch(() => {});

  useEffect(() => {
    let cancelled = false;
    api.adminAutoImportedCounts().then((c) => { if (!cancelled) setCounts(c); }).catch(() => {});
    api.adminAutoListSources().then((r) => { if (!cancelled) setSources(r.items || []); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const runAll = async () => {
    if (runningAll) return;
    const active = sources.filter((s) => s.status === "active");
    if (active.length === 0) {
      toast.error("No hay fuentes activas para ejecutar.");
      return;
    }
    setRunningAll(true);
    try {
      const res = await api.adminAutoRunAll();
      toast.success(`Buscando en ${res.queued} fuente(s) — el proceso corre en segundo plano.`);
      setTimeout(() => { refreshCounts(); refreshSources(); }, 3000);
    } catch (e) {
      toast.error("No se pudo iniciar la búsqueda");
    } finally {
      setRunningAll(false);
    }
  };

  return (
    <div data-testid={ATID.automationPage} className="max-w-7xl">
      <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-textgray">Panel</div>
          <h1 className="text-3xl font-black text-nightblue mt-1 font-serif flex items-center gap-2">
            <Bot className="w-6 h-6" /> Automatización
          </h1>
          <p className="text-sm text-textgray mt-1 max-w-2xl">
            Motor de detección y extracción de noticias desde fuentes externas.
            La reescritura con IA y la publicación automática se activarán en la próxima fase —
            todo lo detectado queda en la bandeja para revisión manual.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="mt-2 border-turquoise/40 text-turquoise bg-turquoise/5">
            Fase 3 · motor activo · con IA
          </Badge>
          <Button
            onClick={runAll}
            disabled={runningAll}
            data-testid={ATID.automationRunAll}
            className="gap-2"
          >
            {runningAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            Buscar noticias ahora
          </Button>
        </div>
      </header>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap gap-2 bg-transparent h-auto p-0 mb-4">
          {TABS.map((t) => (
            <TabsTrigger
              key={t.key}
              value={t.key}
              data-testid={ATID.automationTab(t.key)}
              className="border border-softblue bg-white data-[state=active]:bg-nightblue data-[state=active]:text-white h-9 px-3 text-xs uppercase tracking-widest"
            >
              {t.label}
              {t.key === "inbox" && counts.total > 0 && (
                <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-turquoise/20 text-turquoise data-[state=active]:bg-white/20 data-[state=active]:text-white">
                  {counts.total}
                </span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="dashboard">
          <AutomationDashboardTab sources={sources} onRunAll={runAll} runningAll={runningAll} />
        </TabsContent>
        <TabsContent value="sources">
          <AutomationSourcesTab sources={sources} onChanged={refreshSources} />
        </TabsContent>
        <TabsContent value="inbox">
          <AutomationInboxTab
            sources={sources}
            counts={counts}
            onChanged={() => { refreshCounts(); }}
          />
        </TabsContent>
        <TabsContent value="review">
          <AutomationReviewTab sources={sources} onChanged={refreshCounts} />
        </TabsContent>
        <TabsContent value="runs">
          <AutomationRunsTab sources={sources} />
        </TabsContent>
        <TabsContent value="settings">
          <AutomationSettingsTab />
        </TabsContent>
        <TabsContent value="ai">
          <AutomationAiConfigTab />
        </TabsContent>
        <TabsContent value="logs">
          <AutomationLogsTab sources={sources} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
